import type { FixtureDescriptor } from "./fixtures";
import { compareGuideAwareReadiness } from "../../shared/queue-priority";

export type QueueLens = "anything-useful" | "quick-wins";

const assignmentPriority = {
  "requested-reviewer": 3,
  "requested-team": 3,
  assignee: 2,
  "general-pool": 1,
  author: 0,
  "other-reviewers-requested": 0,
} as const;

/**
 * Keep GitHub routing and coarse wait age authoritative. A current authored
 * guide wins within an age cohort; display lenses may only reorder work that
 * remains equivalent after that incentive.
 */
export function compareQueue(lens: QueueLens, left: FixtureDescriptor, right: FixtureDescriptor): number {
  const reviewability = Number(right.reviewable !== false) - Number(left.reviewable !== false);
  if (reviewability !== 0) return reviewability;

  const assignmentOrder = assignmentPriority[right.assignment?.reason ?? "general-pool"]
    - assignmentPriority[left.assignment?.reason ?? "general-pool"];
  if (assignmentOrder !== 0) return assignmentOrder;

  const claimPriority = { owned: 2, available: 1, held: 0 } as const;
  const claimOrder = claimPriority[right.claim?.status ?? "available"]
    - claimPriority[left.claim?.status ?? "available"];
  if (claimOrder !== 0) return claimOrder;

  const readinessOrder = compareGuideAwareReadiness(
    left.queue.waitingSince,
    right.queue.waitingSince,
    left.chewfile?.status === "guided",
    right.chewfile?.status === "guided",
  );
  if (readinessOrder !== 0) return readinessOrder;

  return lens === "quick-wins"
    ? left.queue.estimatedMinutes - right.queue.estimatedMinutes
    : right.queue.score - left.queue.score;
}
